import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { UserIcon as UserIconSolid } from "@heroicons/react/24/solid";
import { getRooms } from "../../apis/room";

// Custom Bed Icon Component
const BedIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Bed frame */}
    <rect x="3" y="8" width="18" height="10" rx="1" strokeWidth="2" />
    {/* Pillow */}
    <rect x="5" y="6" width="6" height="3" rx="0.5" strokeWidth="1.5" />
    {/* Headboard */}
    <line x1="3" y1="8" x2="3" y2="4" strokeWidth="2" strokeLinecap="round" />
    <line x1="21" y1="8" x2="21" y2="4" strokeWidth="2" strokeLinecap="round" />
    <line x1="3" y1="4" x2="21" y2="4" strokeWidth="2" />
  </svg>
);

interface Bed {
  id: number;
  bedNumber: string;
  status: "available" | "occupied" | "maintenance";
  patientName?: string;
  patientId?: number;
}

interface Room {
  id: number;
  roomNumber: string;
  roomType: string;
  floor: number;
  beds: Bed[];
  status: "available" | "occupied" | "maintenance";
}

interface Floor {
  id: number;
  floorNumber: number;
  rooms: Room[];
}

function buildFloorsFromRooms(roomsRaw: any[]): Floor[] {
  const rooms: Room[] = (roomsRaw || []).map((r: any) => {
    const floorNum = Number(r.floor) || 0;
    const bedsList = r.beds ?? [];
    const beds: Bed[] = bedsList.map((b: any) => {
      // API may send is_available as boolean false, number 0, or string "0" for occupied
      const isAvailable = b.isAvailable === true || b.isAvailable === 1 || String(b.isAvailable) === "1";
      return {
        id: b.id,
        bedNumber: b.name ?? String(b.id),
        status: isAvailable ? "available" : "occupied",
        patientName: b.patient_name ?? b.patientName,
        patientId: b.patient_id ?? b.patientId,
      };
    });
    // Room is "occupied" only when all beds are occupied; otherwise show as available (so multi-bed rooms don't appear fully occupied when only one bed is taken)
    const roomStatus: Room["status"] =
      r.status === "maintenance"
        ? "maintenance"
        : beds.length > 0 && beds.every((b: Bed) => b.status === "occupied")
        ? "occupied"
        : "available";
    return {
      id: r.id,
      roomNumber: r.name ?? String(r.id),
      roomType: r.roomType?.name ?? r.room_type?.name ?? "—",
      floor: floorNum,
      status: roomStatus,
      beds,
    };
  });

  const byFloor = new Map<number, Room[]>();
  rooms.forEach((room) => {
    const f = room.floor;
    if (!byFloor.has(f)) byFloor.set(f, []);
    byFloor.get(f)!.push(room);
  });

  const floorNumbers = Array.from(byFloor.keys()).sort((a, b) => a - b);
  return floorNumbers.map((floorNumber, index) => ({
    id: index + 1,
    floorNumber,
    rooms: byFloor.get(floorNumber) ?? [],
  }));
}

const BedLayout: React.FC = () => {
  const navigate = useNavigate();
  const [floorsData, setFloorsData] = useState<Floor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFloor, setSelectedFloor] = useState<number>(1);
  const [selectedRoom, setSelectedRoom] = useState<number | null>(null);
  const [selectedBed, setSelectedBed] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRooms({ per_page: 1000 })
      .then((res: any) => {
        if (cancelled) return;
        const list = res?.data?.data ?? res?.data ?? res ?? [];
        const arr = Array.isArray(list) ? list : [];
        const floors = buildFloorsFromRooms(arr);
        setFloorsData(floors);
        if (floors.length > 0 && !floors.some((f) => f.floorNumber === selectedFloor)) {
          setSelectedFloor(floors[0].floorNumber);
        }
      })
      .catch(() => {
        if (!cancelled) setFloorsData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (floorsData.length === 0) return;
    const hasCurrent = floorsData.some((f) => f.floorNumber === selectedFloor);
    if (!hasCurrent) setSelectedFloor(floorsData[0].floorNumber);
  }, [floorsData, selectedFloor]);

  const currentFloor = useMemo(
    () => floorsData.find((f) => f.floorNumber === selectedFloor) ?? floorsData[0],
    [floorsData, selectedFloor]
  );

  const getBedStatusColor = (status: Bed["status"]) => {
    switch (status) {
      case "available":
        return "bg-green-100 border-green-400 text-green-700 hover:bg-green-200";
      case "occupied":
        return "bg-red-100 border-red-400 text-red-700 hover:bg-red-200";
      case "maintenance":
        return "bg-yellow-100 border-yellow-400 text-yellow-700 hover:bg-yellow-200";
      default:
        return "bg-gray-100 border-gray-400 text-gray-700";
    }
  };

  const getRoomStatusColor = (status: Room["status"]) => {
    switch (status) {
      case "available":
        return "border-green-300 bg-green-50";
      case "occupied":
        return "border-red-300 bg-red-50";
      case "maintenance":
        return "border-yellow-300 bg-yellow-50";
      default:
        return "border-gray-300 bg-gray-50";
    }
  };

  const getStatusIcon = (status: Bed["status"]) => {
    switch (status) {
      case "available":
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case "occupied":
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case "maintenance":
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const totalStats = useMemo(() => {
    let totalBeds = 0;
    let availableBeds = 0;
    let occupiedBeds = 0;
    let maintenanceBeds = 0;

    floorsData.forEach((floor) => {
      floor.rooms.forEach((room) => {
        room.beds.forEach((bed) => {
          totalBeds++;
          if (bed.status === "available") availableBeds++;
          else if (bed.status === "occupied") occupiedBeds++;
          else if (bed.status === "maintenance") maintenanceBeds++;
        });
      });
    });

    return { totalBeds, availableBeds, occupiedBeds, maintenanceBeds };
  }, [floorsData]);

  const handleBedClick = (bed: Bed, room: Room) => {
    setSelectedBed(bed.id);
    setSelectedRoom(room.id);
    // TODO: Open bed details modal or navigate to patient details
    console.log("Bed clicked:", bed, "Room:", room);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BuildingOfficeIcon className="w-7 h-7 text-primary" />
              Bed Management Layout
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Visual overview of all beds and their occupancy status
            </p>
          </div>
          <button
            type="button"
            disabled={true}
            onClick={() => navigate("/bed-layout-3d")}
            className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            View 3D Layout
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Beds</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalStats.totalBeds}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <RectangleStackIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{totalStats.availableBeds}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Occupied</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{totalStats.occupiedBeds}</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <XCircleIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Maintenance</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{totalStats.maintenanceBeds}</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floor Selector */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-semibold text-gray-700">Select Floor:</span>
          <div className="flex gap-2">
            {floorsData.map((floor) => (
              <button
                key={floor.id}
                onClick={() => {
                  setSelectedFloor(floor.floorNumber);
                  setSelectedRoom(null);
                  setSelectedBed(null);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  selectedFloor === floor.floorNumber
                    ? "bg-primary text-white shadow-md transform scale-105"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                }`}
              >
                Floor {floor.floorNumber}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">Legend:</p>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-green-400 bg-green-100"></div>
            <span className="text-sm text-gray-600">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-red-400 bg-red-100"></div>
            <span className="text-sm text-gray-600">Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-yellow-400 bg-yellow-100"></div>
            <span className="text-sm text-gray-600">Maintenance</span>
          </div>
        </div>
      </div>

      {/* Rooms Grid */}
      <div className="bg-gray-50 rounded-xl p-6 min-h-[600px]">
        <div className="mb-4 flex items-center gap-2">
          <BuildingOfficeIcon className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-800">
            Floor {currentFloor?.floorNumber ?? selectedFloor} - {currentFloor?.rooms?.length ?? 0} Rooms
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-500">
            Loading beds and rooms...
          </div>
        ) : !currentFloor || currentFloor.rooms.length === 0 ? (
          <div className="flex items-center justify-center py-24 text-gray-500">
            No rooms on this floor. Add rooms from the Rooms list.
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentFloor.rooms.map((room) => (
            <div
              key={room.id}
              className={`bg-white rounded-xl shadow-md border-2 transition-all duration-200 hover:shadow-xl hover:scale-105 ${
                selectedRoom === room.id
                  ? "ring-4 ring-primary ring-opacity-50"
                  : ""
              } ${getRoomStatusColor(room.status)}`}
            >
              {/* Room Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Room {room.roomNumber}</h3>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                    {room.roomType}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  {getStatusIcon(
                    room.beds.some((b) => b.status === "occupied")
                      ? "occupied"
                      : room.beds.some((b) => b.status === "maintenance")
                      ? "maintenance"
                      : "available"
                  )}
                  <span>
                    {room.beds.filter((b) => b.status === "occupied").length} / {room.beds.length}{" "}
                    occupied
                  </span>
                </div>
              </div>

              {/* Beds Grid */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {room.beds.map((bed) => (
                    <button
                      key={bed.id}
                      onClick={() => handleBedClick(bed, room)}
                      className={`relative p-4 rounded-lg border-2 transition-all duration-200 transform hover:scale-110 ${getBedStatusColor(
                        bed.status
                      )} ${
                        selectedBed === bed.id ? "ring-2 ring-primary ring-offset-2" : ""
                      }`}
                    >
                      {/* Bed Icon */}
                      <div className="flex flex-col items-center justify-center gap-2">
                        {bed.status === "occupied" ? (
                          <UserIconSolid className="w-8 h-8" />
                        ) : (
                          <BedIcon className="w-8 h-8" />
                        )}
                        <span className="text-sm font-semibold">Bed {bed.bedNumber}</span>
                        {bed.status === "occupied" && bed.patientName && (
                          <span className="text-xs font-medium mt-1 truncate max-w-full">
                            {bed.patientName}
                          </span>
                        )}
                      </div>

                      {/* Status Badge */}
                      <div className="absolute top-1 right-1">
                        {getStatusIcon(bed.status)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Selected Bed Info */}
      {selectedBed && selectedRoom && currentFloor?.rooms && (
        <div className="mt-6 bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Selected Bed Information</h3>
          {(() => {
            const room = currentFloor.rooms.find((r) => r.id === selectedRoom);
            const bed = room?.beds.find((b) => b.id === selectedBed);
            if (!bed) return null;
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Room:</span>
                  <p className="text-gray-900">{room?.roomNumber}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Bed:</span>
                  <p className="text-gray-900">Bed {bed.bedNumber}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Status:</span>
                  <p className="text-gray-900 capitalize">{bed.status}</p>
                </div>
                {bed.patientName && (
                  <div>
                    <span className="font-medium text-gray-600">Patient:</span>
                    <p className="text-gray-900">{bed.patientName}</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default BedLayout;

